import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';

// Use the User type from authService
import type { User } from '../services/authService';
import { getUserById } from '../services/user.service';

interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  refreshUser: () => Promise<void>;
  logout: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUserState] = useState<User | null>(null);
  const userRef = useRef<User | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Load user from localStorage on mount, then refresh from backend once
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      setUserState(parsed);
      userRef.current = parsed;
      if (parsed?.id) {
        getUserById(parsed.id)
          .then((profile) => {
            const updated: User = {
              ...parsed,
              country: profile.country,
              state: profile.state,
              role: profile.role,
              company: profile.company,
              onboarded: profile.onboarded,
              credits: profile.credits,
              subscriptionStatus: profile.subscriptionStatus,
              subscriptionId: profile.subscriptionId,
              stripeCustomerId: profile.stripeCustomerId,
              subscriptionEndDate: profile.subscriptionEndDate,
            };
            setUserState(updated);
            userRef.current = updated;
            localStorage.setItem('user', JSON.stringify(updated));
          })
          .catch((err) => console.error('Error refreshing user on mount:', err));
      }
    }
  }, []);

  // Sync user to localStorage
  const setUser = useCallback((user: User | null) => {
    setUserState(user);
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, []);

  // Refresh user data from the backend - stable function that doesn't change
  const refreshUser = useCallback(async () => {
    const currentUser = userRef.current;
    if (!currentUser?.id) {
      console.warn('No user ID available to refresh');
      return;
    }

    try {
      const userProfile = await getUserById(currentUser.id);
      
      // Update user with fresh data from backend
      const updatedUser: User = {
        ...currentUser,
        country: userProfile.country,
        state: userProfile.state,
        role: userProfile.role,
        company: userProfile.company,
        onboarded: userProfile.onboarded,
        credits: userProfile.credits,
        subscriptionStatus: userProfile.subscriptionStatus,
        subscriptionId: userProfile.subscriptionId,
        stripeCustomerId: userProfile.stripeCustomerId,
        subscriptionEndDate: userProfile.subscriptionEndDate,
      };
      
      setUser(updatedUser);
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  }, [setUser]);

  const logout = () => {
    setUser(null);
  };

  return (
    <UserContext.Provider value={{ user, setUser, refreshUser, logout }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}; 